import * as React from 'react';
import { cn } from '../utils/cn';

export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('lectura-card overflow-hidden', className)} {...props} />
  ),
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 px-6 pt-6 pb-4', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('font-display text-lg font-semibold leading-snug tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-[var(--color-foreground-muted)]', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('px-6 pb-6', className)} {...props} />,
);
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center gap-3 border-t border-[var(--color-border)] bg-[var(--color-surface-muted)] px-6 py-4',
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

/**
 * Könyv-kártya — tipikus listaelem az olvasó és admin oldalon is.
 * A borító 2:3 arányban jelenik meg (ePUB szabvány).
 */
export interface BookCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  authors?: string[];
  coverUrl?: string | null;
  badge?: React.ReactNode;
  meta?: React.ReactNode;
  href?: string;
}

export const BookCard = React.forwardRef<HTMLDivElement, BookCardProps>(
  ({ title, authors, coverUrl, badge, meta, className, ...props }, ref) => (
    <Card ref={ref} className={cn('group flex flex-col', className)} {...props}>
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-gradient-to-br from-brand-100 to-brand-200">
        {coverUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={coverUrl}
            alt={`${title} borítója`}
            loading="lazy"
            className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center font-display text-lg font-semibold text-brand-700">
            {title}
          </div>
        )}
        {badge ? <div className="absolute right-3 top-3">{badge}</div> : null}
      </div>
      <div className="flex flex-1 flex-col gap-1 px-4 pb-4 pt-4">
        <h4 className="line-clamp-2 font-display text-sm font-semibold leading-tight">{title}</h4>
        {authors && authors.length > 0 ? (
          <p className="line-clamp-1 text-xs text-[var(--color-foreground-muted)]">{authors.join(', ')}</p>
        ) : null}
        {meta ? <div className="mt-auto pt-2 text-xs text-[var(--color-foreground-subtle)]">{meta}</div> : null}
      </div>
    </Card>
  ),
);
BookCard.displayName = 'BookCard';
